import { ActionTree } from 'vuex';
import { EFieldSource } from '../types/enums/field-source';
import { ISelectedPageFieldIds } from '../types/selected-page-field-ids';
import { lastUpdatedField } from './field.getters';
import { namespace as dealroomNamespace } from '@/dealroom/store/dealroom.store';
import { EAlignFieldType } from '@/fields/context-menu/types/enums/align-field-type';
import { createDefaultField } from '@/fields/helpers/field-default.helper';
import {
  calculateFieldContainerPosition,
  calculateFieldOffset,
  correctFieldPosition,
} from '@/fields/helpers/field-placement.helper';
import service from '@/fields/services/field.service';
import {
  mutationAddFields,
  mutationDeleteFields,
  mutationDeselectAllFields,
  mutationDeselectField,
  mutationSelectField,
  mutationSetDocumentId,
  mutationSetFields,
  mutationSetFocusedFieldId,
  mutationSetLoading,
  mutationSetPageWidth,
  mutationSetSelectedFields,
  mutationSetSenderSignature,
  mutationSetTabFieldId,
  mutationUpdateField,
  mutationUpdateFieldDone,
  mutationSetLastUpdatedFieldId,
  mutationSetSelectBoxFields,
  mutationSetSelectedPageFieldIds,
} from '@/fields/store/field.mutations';
import { IFieldState, state as defaultState } from '@/fields/store/field.store';
import { EFieldType } from '@/fields/types/enums/field-type';
import { EMergeValue } from '@/fields/types/enums/merge-value';
import { IField } from '@/fields/types/field';
import { IFieldPosition } from '@/fields/types/field-position';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { namespace as alertNamespace } from '@/store/stores/alert.store';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { IUser } from '@/types/User';
import { EnumDocumentType } from '@/enums/DocumentType';
import { getterConnectFirstRecipient } from '@/dealroom/store/dealroom.getters';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { FieldHelper } from '@/fields/helpers/field.helper';

export const actionLoadFields = 'loadFields';
export const actionLoadFieldsSuccess = 'loadFieldsSuccess';
export const actionLoadFieldsFailed = 'loadFieldsFailed';

export const actionCreateField = 'createField';
export const actionCreateFieldSuccess = 'createFieldSuccess';
export const actionCreateFieldFailed = 'createFieldFailed';

export const actionUpdateFields = 'updateFields';
export const actionUpdateFieldFailed = 'updateFieldFailed';

export const actionSelectField = 'selectField';
export const actionDeselectField = 'deselectField';
export const actionDeselectAllFields = 'deselectAllFields';

export const actionTabField = 'tabField';
export const actionFocusField = 'focusField';

export const actionDeleteFields = 'deleteFields';
export const actionDeleteFieldsFailed = 'deleteFieldsFailed';

export const actionDeleteSignatureFields = 'deleteSignatureFields';

export const actionDuplicateFields = 'duplicateFields';
export const actionDuplicateFieldsFailed = 'duplicateFieldsFailed';

export const actionChangeTextSize = 'changeTextSize';
export const actionChangeTextSizeFailed = 'changeTextSizeFailed';

export const actionUpdatePageWidth = 'updatePageWidth';
export const actionAlignFields = 'alignFields';
export const actionAlignFieldsFailed = 'alignFieldsFailed';
export const actionMoveFields = 'moveFields';

export const actionLoadSenderSignature = 'loadSenderSignature';
export const actionLoadSenderSignatureSuccess = 'loadSenderSignatureSuccess';
export const actionLoadSenderSignatureFailed = 'loadSenderSignatureFailed';

export const actionCorrectFieldPosition = 'correctFieldPosition';
export const actionGetPageDimensions = 'getPageDimensions';

export const actionSetLastUpdatedFieldId = 'setLastUpdatedFieldId';

export const actionSetSelectBoxFields = 'setSelectBoxFields';
export const actionEndSelectBoxFields = 'endSelectBoxFields';

export const actions: ActionTree<IFieldState, RootState> = {
  async [actionLoadFields]({ dispatch, commit }, documentId: string) {
    commit(mutationSetDocumentId, documentId);
    commit(mutationSetLoading, true);
    try {
      const fields: IField[] = await service.loadFields(documentId);
      const mappedFields: IField[] = fields.map((field: IField) =>
        field.type === EFieldType.Merge
          ? {
              ...field,
              displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
            }
          : field
      );
      dispatch(actionLoadFieldsSuccess, mappedFields);
    } catch (err) {
      dispatch(actionLoadFieldsFailed);
    }
  },
  async [actionLoadFieldsSuccess]({ commit }, fields: IField[]) {
    commit(mutationSetFields, fields);
    commit(mutationSetLoading, false);
  },
  async [actionLoadFieldsFailed]({ commit }) {
    commit(mutationSetLoading, false);
  },
  async [actionCreateField](
    { commit, dispatch, state, getters, rootGetters },
    {
      documentPage,
      documentId,
      documentType,
      type,
      mergeValue,
      sender,
      recipient,
      top,
      left,
    }: {
      documentPage: IDocumentPage;
      documentId: string;
      documentType: string;
      type: EFieldType;
      mergeValue?: EMergeValue;
      sender?: IUser;
      recipient?: IRecipient;
      top?: number;
      left?: number;
    }
  ) {
    const lastUpdatedFieldHasSamePage: boolean =
      getters[lastUpdatedField] && documentPage.id === getters[lastUpdatedField].pageId;
    const field: IField = createDefaultField(
      documentPage,
      documentId,
      type,
      state.pageWidth,
      rootGetters[`${dealroomNamespace}/${getterConnectFirstRecipient}`],
      mergeValue,
      sender,
      recipient && recipient.id,
      top,
      left,
      lastUpdatedFieldHasSamePage ? getters[lastUpdatedField] : null
    );
    field.position = calculateFieldOffset(state.fields, field);
    field.position = correctFieldPosition(field.position, field.pageId, state.pageWidth);
    field.isLoading = true;
    field.id = FieldHelper.getTempId();
    field.isEditable = true;
    // If the field is created from a template the user should not be able to alter the field when creating a document based on the template
    if (documentType === EnumDocumentType.Template) {
      field.isEditable = false;
    }

    commit(mutationAddFields, [field]);
    try {
      const id: string = await service.createField(field);
      const savedField: IField = { ...field, id, isLoading: false };
      commit(mutationUpdateFieldDone, {
        field: savedField,
        tempId: field.id,
      });
      dispatch(actionCreateFieldSuccess, savedField);
    } catch {
      commit(mutationDeleteFields, [field.id]);
      dispatch(actionCreateFieldFailed);
    }
  },
  async [actionCreateFieldFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-create-field'), {
      root: true,
    });
  },
  async [actionCreateFieldSuccess]({ dispatch }, field: IField) {
    if (field.type === EFieldType.Merge) {
      field = {
        ...field,
        displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
      };
    }
    if (field.type === EFieldType.Initials || field.type === EFieldType.Signature) {
      const payload: Partial<IDocument> = {
        isSigning: 1,
      };
      if (field.source === EFieldSource.Sender) {
        payload.isSelfsign = 1;
      }
      if (field.type === EFieldType.Signature) {
        payload.isSigningBiometric = 1;
      }
      if (field.type === EFieldType.Initials) {
        payload.isSigningInitials = 1;
      }
      dispatch(`${dealroomNamespace}/updateDocument`, payload, { root: true });
    }
    dispatch(actionSetLastUpdatedFieldId, [field]);
  },
  async [actionUpdateFields]({ commit, state, dispatch }, fields: IField[]) {
    const previousFields: IField[] = state.fields;
    const updatedFields: IField[] = previousFields.map((stateField: IField) =>
      fields.some((field: IField) => field.id === stateField.id)
        ? fields.find((field: IField) => field.id === stateField.id)
        : stateField
    );
    dispatch(actionSetLastUpdatedFieldId, fields);
    commit(mutationSetFields, updatedFields);
    try {
      await service.updateFields(fields);
    } catch {
      dispatch(actionUpdateFieldFailed, previousFields);
    }
  },
  async [actionUpdateFieldFailed]({ commit, dispatch }, previousField: IField) {
    commit(mutationUpdateField, previousField);
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-update-field'), {
      root: true,
    });
  },
  async [actionSelectField]({ commit }, field: IField) {
    commit(mutationSelectField, field);
  },
  async [actionDeselectField]({ commit }, field: IField) {
    commit(mutationDeselectField, field);
  },
  async [actionTabField]({ commit, state }, pages: IDocumentPage[]) {
    const { fieldIds } = state.selectedPageFieldIds;
    const nextField: IField = FieldHelper.getNextTabField(state.fields, fieldIds, pages);
    commit(mutationSetSelectedFields, [nextField]);
    commit(mutationSetTabFieldId, nextField.id);
  },
  async [actionDeleteFields]({ commit, dispatch, state }) {
    const fieldsToBeDeleted: IField[] = state.fields.filter((field: IField) =>
      state.selectedPageFieldIds.fieldIds.some((id: string) => id === field.id)
    );
    commit(mutationDeleteFields, state.selectedPageFieldIds.fieldIds);
    try {
      await service.deleteFields(state.selectedPageFieldIds.fieldIds);
    } catch {
      dispatch(actionDeleteFieldsFailed, fieldsToBeDeleted);
    }
    commit(mutationDeselectAllFields);
  },
  async [actionDeleteFieldsFailed]({ commit, dispatch }, fields: IField[]) {
    commit(mutationAddFields, fields);
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-delete-field'), {
      root: true,
    });
  },
  async [actionDeleteSignatureFields]({ commit, state }) {
    const fieldsToBeDeleted: IField[] = FieldHelper.getSignatureFields(state.fields);
    const fieldIds: string[] = fieldsToBeDeleted.map((field: IField) => field.id);
    commit(mutationDeleteFields, fieldIds);
    try {
      await service.deleteFields(fieldIds);
    } catch {
      this.dispatch(actionDeleteFieldsFailed, fieldsToBeDeleted);
    }
  },
  async [actionDeselectAllFields]({ commit }) {
    commit(mutationDeselectAllFields);
  },
  async [actionDuplicateFields]({ commit, dispatch, state }) {
    try {
      const fieldsToCopy: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((field: IField) => {
          const fieldToCopy: IField = { ...field };
          delete fieldToCopy.id;
          const duplicatePosition: IFieldPosition = FieldHelper.getDuplicateFieldPosition(field);

          return {
            ...fieldToCopy,
            position: correctFieldPosition(duplicatePosition, field.pageId, state.pageWidth),
          };
        });
      const copiedFields: IField[] = await service.createFields(fieldsToCopy);
      commit(mutationAddFields, copiedFields);
      commit(mutationSetSelectedFields, copiedFields);
      dispatch(actionSetLastUpdatedFieldId, copiedFields);
    } catch (e) {
      dispatch(actionDuplicateFieldsFailed);
    }
  },
  async [actionDuplicateFieldsFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-copy-field'), {
      root: true,
    });
  },
  async [actionUpdatePageWidth]({ commit }, pageWidth: number) {
    commit(mutationSetPageWidth, pageWidth);
  },
  async [actionAlignFields]({ state, dispatch }, actionType: EAlignFieldType) {
    const fields: IField[] = state.fields.filter((field: IField) =>
      state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
    );
    try {
      const fieldContainerPosition: IFieldPosition = calculateFieldContainerPosition(fields);
      const updatedFields: IField[] = FieldHelper.getAlignedFields(
        fields,
        actionType,
        fieldContainerPosition
      );
      dispatch(actionUpdateFields, updatedFields);
    } catch (e) {
      dispatch(actionAlignFieldsFailed);
    }
  },
  async [actionAlignFieldsFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-align-fields'), {
      root: true,
    });
  },
  async [actionMoveFields]({ dispatch, state }, pageId: string) {
    const movedFields: IField[] = state.fields
      .filter((field: IField) =>
        state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
      )
      .map((field: IField) => ({
        ...field,
        pageId,
        position: correctFieldPosition(field.position, pageId, state.pageWidth),
      }));

    dispatch(actionUpdateFields, movedFields);
    dispatch(actionDeselectAllFields);
  },
  async [actionChangeTextSize]({ dispatch, state }, height: number) {
    try {
      const updatedFields: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((selectedField: IField) => {
          if (selectedField.position.height !== height && selectedField.type !== EFieldType.Check) {
            selectedField.position.height = height;
          }
          return selectedField;
        });
      dispatch(actionUpdateFields, updatedFields);
    } catch (e) {
      dispatch(actionChangeTextSizeFailed);
    }
  },
  async [actionChangeTextSizeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-change-field-size'), {
      root: true,
    });
  },
  async [actionFocusField]({ commit }, fieldId: string) {
    commit(mutationSetFocusedFieldId, fieldId);
  },
  async [actionLoadSenderSignature]({ dispatch }, userId: string) {
    try {
      const signatureData: string = await service.loadUserSignature(userId);
      dispatch(actionLoadSenderSignatureSuccess, signatureData);
    } catch (e) {
      dispatch(actionLoadSenderSignatureFailed);
    }
  },
  async [actionLoadSenderSignatureSuccess]({ commit }, signatureData: string) {
    commit(mutationSetSenderSignature, signatureData);
  },
  async [actionLoadSenderSignatureFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-load-signature'), {
      root: true,
    });
  },
  [actionSetLastUpdatedFieldId]({ commit }, updatedFields: IField[]) {
    const sortedUpdatedFields: IField[] = FieldHelper.sortByPositionWithoutPages(updatedFields);
    const lastUpdatedField: IField = sortedUpdatedFields[sortedUpdatedFields.length - 1];
    commit(mutationSetLastUpdatedFieldId, lastUpdatedField.id);
  },
  [actionSetSelectBoxFields]({ commit }, selectedPageFieldIds: ISelectedPageFieldIds) {
    commit(mutationSetSelectBoxFields, selectedPageFieldIds);
  },
  [actionEndSelectBoxFields]({ commit, state }) {
    commit(mutationSetSelectedPageFieldIds, state.markedPageFieldIds);
    commit(mutationSetSelectBoxFields, defaultState.markedPageFieldIds);
  },
};
